/**
 * The file dialogs' remembered preferences — per viewer, in this browser only.
 *
 *   - view and sort — how the user likes to browse (`fias.files.view`,
 *     `fias.files.sort`);
 *   - last location — where they last saved or opened FOR EACH ARCHE
 *     (`fias.files.last.<userId>.<archeKey>`), so a Save from the word
 *     processor reopens in the folder they used for it last time.
 *
 * Browser storage is a convenience, never state that must survive: it can be
 * empty or throw (private windows, blocked site data, tests), so every access
 * is wrapped and the dialogs work without it. The last location is host-only —
 * it is never sent to an arche.
 */
import type { VaultFileDialogArea } from '@/lib/api';

export type FileDialogView = 'list' | 'grid';
export type FileDialogSortKey = 'updated' | 'name' | 'size' | 'kind';
export interface FileDialogSort {
  sort: FileDialogSortKey;
  direction: 'asc' | 'desc';
}

export interface FileDialogLastLocation {
  holderId: string;
  area: VaultFileDialogArea;
  folderId: string | null;
}

const VIEW_KEY = 'fias.files.view';
const SORT_KEY = 'fias.files.sort';
const LAST_PREFIX = 'fias.files.last.';

export const DEFAULT_SORT: FileDialogSort = { sort: 'updated', direction: 'desc' };

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // A preference that does not stick is not an error worth surfacing.
  }
}

export function readView(): FileDialogView {
  return read(VIEW_KEY) === 'grid' ? 'grid' : 'list';
}

export function writeView(view: FileDialogView): void {
  write(VIEW_KEY, view);
}

const SORT_KEYS: readonly FileDialogSortKey[] = ['updated', 'name', 'size', 'kind'];

export function readSort(): FileDialogSort {
  try {
    const parsed = JSON.parse(read(SORT_KEY) ?? 'null') as Partial<FileDialogSort> | null;
    if (
      parsed &&
      SORT_KEYS.includes(parsed.sort as FileDialogSortKey) &&
      (parsed.direction === 'asc' || parsed.direction === 'desc')
    ) {
      return { sort: parsed.sort as FileDialogSortKey, direction: parsed.direction };
    }
  } catch {
    // Unparseable — fall through to the default.
  }
  return DEFAULT_SORT;
}

export function writeSort(sort: FileDialogSort): void {
  write(SORT_KEY, JSON.stringify(sort));
}

function lastLocationKey(userId: string, archeKey: string): string {
  return `${LAST_PREFIX}${userId}.${archeKey}`;
}

/** Where this user last saved or opened for `archeKey` (an arche id, or a
 *  host surface name). `null` when there is no usable memory. */
export function readLastLocation(userId: string, archeKey: string): FileDialogLastLocation | null {
  if (!userId || !archeKey) return null;
  try {
    const parsed = JSON.parse(
      read(lastLocationKey(userId, archeKey)) ?? 'null',
    ) as Partial<FileDialogLastLocation> | null;
    if (
      parsed &&
      typeof parsed.holderId === 'string' &&
      (parsed.area === 'documents' || parsed.area === 'arche-saves') &&
      (parsed.folderId === null || typeof parsed.folderId === 'string')
    ) {
      return { holderId: parsed.holderId, area: parsed.area, folderId: parsed.folderId };
    }
  } catch {
    // Unparseable — no memory.
  }
  return null;
}

export function writeLastLocation(
  userId: string,
  archeKey: string,
  location: FileDialogLastLocation,
): void {
  if (!userId || !archeKey) return;
  write(lastLocationKey(userId, archeKey), JSON.stringify(location));
}
